import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useVariantAuditLogs } from '@/api/audit-logs'
import { useCogsBreakdown } from '@/api/cogs'
import { useVariant } from '@/api/variants'
import { useVendorPriceHistory } from '@/api/vendor-prices'
import type { VariantDetail } from '@/api/variants'
import { useWorkspace } from '@/api/workspace'
import { SuggestedPrice } from '@/components/cogs/suggested-price'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { RecordHistory } from '@/components/record-history/record-history'
import { MovementList } from '@/components/stock/movement-list'
import { VariantPricesCard } from '@/components/vendor-prices/variant-prices-card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// Variant detail (UI-SCREENS P-10, 05b #170): stock per location — Buildable, Gudang (Home),
// Stok di kanal per Sales Channel and Total — the Variant's movement history, "needs color", and
// the Owner-only History section, which moves here off P-09 (#98 Q16). Actions: Assemble (F-10)
// and Edit; Dispatch (I-05) joins once 05c #171 lands. An archived channel keeps its figure until
// its stock is 0 (#176).
//
// Owner and Staff both see the page; only the Owner sees History and may edit the Variant.
export const Route = createFileRoute('/w/$slug/variants/$variantId/')({
  component: VariantDetailPage,
  staticData: { breadcrumb: 'variants.title' },
})

function VariantDetailPage() {
  const { t } = useTranslation()
  const { slug, variantId } = Route.useParams()
  const workspace = useWorkspace(slug)
  const variant = useVariant(slug, Number(variantId))
  const isOwner = workspace.data?.role === 'owner'
  const history = useVariantAuditLogs(slug, Number(variantId), { enabled: isOwner })

  if (variant.isPending) return <LoadingSkeleton variant="page" />
  if (variant.isError || !variant.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const detail = variant.data

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/stock" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('stock.detail.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">
        {detail.sku} · {detail.name}
      </h1>
      <div className="flex flex-wrap gap-2 empty:hidden">
        {detail.archived_at ? <Badge variant="outline">{t('stock.archived')}</Badge> : null}
        {detail.needs_color ? <Badge variant="outline">{t('stock.needsColor')}</Badge> : null}
      </div>

      <StockCard detail={detail} />
      <CogsCard slug={slug} variantId={detail.id} isOwner={isOwner} />
      {isOwner ? <VariantPricesCard slug={slug} variantId={detail.id} /> : null}
      <Actions slug={slug} detail={detail} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('stock.movements.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <MovementList slug={slug} filters={{ stockItemId: detail.stock.stock_item_id }} showItem={false} />
        </CardContent>
      </Card>

      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}

// Stock per location. Buildable is never added into Total: Variants share WIP (#55).
function StockCard({ detail }: { detail: VariantDetail }) {
  const { t } = useTranslation()
  const { buildable, with_substitutes: withSubstitutes, buildable_reason: reason, home, channels, total } = detail.stock
  const substitutes = buildable !== null && withSubstitutes !== null ? withSubstitutes - buildable : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('stock.detail.stock')}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* One figure per Sales Channel; they scroll sideways on a phone (#176 Q1). */}
        <dl className="flex gap-x-6 overflow-x-auto text-sm">
          <Figure
            label={t('stock.columns.buildable')}
            value={buildable === null ? '—' : formatQty(buildable)}
            hint={
              reason !== null
                ? t(`stock.reasons.${reason}`, { defaultValue: '' })
                : substitutes > 0
                  ? t('stock.withSubstitutes', { count: substitutes })
                  : undefined
            }
          />
          <Figure label={t('stock.columns.home')} value={formatQty(home)} />
          {channels.map((channel) => (
            <Figure
              key={channel.sales_channel_id}
              label={channel.name}
              value={formatQty(channel.qty)}
              hint={t('stock.columns.channels')}
              badge={channel.archived ? t('stock.archived') : undefined}
            />
          ))}
          <Figure label={t('stock.columns.total')} value={formatQty(total)} />
        </dl>
      </CardContent>
    </Card>
  )
}

// The COGS block (08c #204): the Variant's COGS, the estimated flag and a link to C-02. Owner and
// Staff. The Owner also sees the Suggested Vendor Price (08d #205) next to the current default
// Vendor Price (#205 Q4); the API omits the suggestion for Staff and answers them 403 on prices.
function CogsCard({ slug, variantId, isOwner }: { slug: string; variantId: number; isOwner: boolean }) {
  const { t } = useTranslation()
  const cogs = useCogsBreakdown(slug, variantId)
  const prices = useVendorPriceHistory(slug, isOwner ? variantId : null)
  const row = cogs.data?.cogs
  const suggested = cogs.data?.suggested_vendor_price
  const defaultPrice = prices.data?.items[0]?.default_price ?? null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('cogs.breakdown.variantBlock')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {cogs.isPending ? (
          <LoadingSkeleton variant="lines" rows={3} />
        ) : cogs.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {t('errors.generic')}
          </p>
        ) : row ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('cogs.cogs')}</span>
            <span className="text-lg font-semibold">Rp {formatMoney(row.total)}</span>
            {row.estimated ? <Badge variant="secondary">{t('cogs.estimated')}</Badge> : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('cogs.notComputable')}</p>
        )}
        {suggested !== undefined ? (
          <>
            <SuggestedPrice price={suggested} hasCogs={Boolean(row)} estimated={row?.estimated ?? false} />
            <div className="text-sm">
              <span className="text-muted-foreground">{t('cogs.vendorPrice')}: </span>
              {defaultPrice ? (
                <span className="font-medium">Rp {formatMoney(defaultPrice.price)}</span>
              ) : (
                <span className="text-muted-foreground">{t('cogs.noVendorPrice')}</span>
              )}
            </div>
          </>
        ) : null}
        <Link
          to="/w/$slug/cogs/$variantId"
          params={{ slug, variantId: String(variantId) }}
          className="text-sm underline underline-offset-4"
        >
          {t('cogs.breakdown.open')}
        </Link>
      </CardContent>
    </Card>
  )
}

function Actions({ slug, detail }: { slug: string; detail: VariantDetail }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-wrap gap-2">
      {detail.archived_at ? null : (
        <Button asChild className="h-11">
          <Link to="/w/$slug/assemblies/new" params={{ slug }} search={{ variantId: detail.id }}>
            {t('stock.detail.assemble')}
          </Link>
        </Button>
      )}
      {detail.can.update ? (
        <Button asChild variant="outline" className="h-11">
          <Link to="/w/$slug/variants/$variantId/edit" params={{ slug, variantId: String(detail.id) }}>
            {t('stock.detail.edit')}
          </Link>
        </Button>
      ) : null}
    </div>
  )
}

function Figure({ label, value, hint, badge }: { label: string; value: string; hint?: string; badge?: string }) {
  return (
    <div className="flex shrink-0 flex-col">
      <dt className="flex items-center gap-1 whitespace-nowrap text-muted-foreground">
        {label}
        {badge ? <Badge variant="outline">{badge}</Badge> : null}
      </dt>
      <dd>{value}</dd>
      {hint ? <dd className="whitespace-nowrap text-xs text-muted-foreground">{hint}</dd> : null}
    </div>
  )
}
